import {
	createLog,
	type FreightServiceConfig,
	type FreightServiceConfigDtoInput,
	type FreightServiceConfigDtoOutput,
	freightServiceConfigDtoSchema,
	INJECTED_FEE_CODES,
	LogSeverity,
	OPERATIONS_SECTIONS,
	PricingModel,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
} from '@ekhodealer/ekho-common';
import { Form } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateFreightService } from '../../../api/configuration/services/useUpdateFreightService';
import { useServiceProgramInfo } from '../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../config-mgmt/FormSection';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): FreightServiceConfigDtoInput {
	const freightConfig = servicesProvider?.config?.[Services.FREIGHT] as
		| FreightServiceConfig
		| undefined;

	if (!freightConfig?.pricing?.length) {
		return {
			supported: false,
			pricing: [
				{
					model: PricingModel.FLAT_FEE,
					modelDetails: {
						flatFee: 0,
					},
				},
			],
		};
	}

	const firstPricing = freightConfig.pricing[0];
	return {
		supported: freightConfig.supported ?? false,
		pricing: [firstPricing],
	};
}

export function FreightServiceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(OPERATIONS_SECTIONS.FREIGHT);
	const updateFreightServiceMutation = useUpdateFreightService();

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (
		data: FreightServiceConfigDtoOutput,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		// Transform DTO to API payload format
		const dataToSubmit = {
			supported: data.supported,
			pricing: [
				{
					...data.pricing[0],
					code: INJECTED_FEE_CODES.FREIGHT_FEE,
					feeDescription: 'Freight fee',
				},
			],
		};

		try {
			await updateFreightServiceMutation.mutateAsync(dataToSubmit);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update freight service configuration: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.OPERATIONS,
				section: OPERATIONS_SECTIONS.FREIGHT,
			}}
			form={{
				schema: freightServiceConfigDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Freight'
			description='Set the default manufacturer freight charge included in the vehicle price at checkout. This covers the cost of shipping the vehicle from the manufacturer to your facility.'
			formTitle='Freight pricing'
			toggleSettings={{
				fieldName: 'supported',
				visibility: 'write',
			}}
			hideDivider>
			<Form.Field.Pricing
				schemaField='pricing'
				supportedModels={[PricingModel.FLAT_FEE]}
				modelLabelText='Manufacturer Freight Fee'
				modelDescriptionText="Freight charge included in the buyer's vehicle price at checkout. This can be customized per vehicle."
			/>
		</FormSection>
	);
}
